import React, { useEffect, useState } from 'react';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import { useDispatch, useSelector } from 'react-redux';
import { getContacts, contactSelector, deleteContact } from '../../../../store/contact/slice';
import { Button, DataTableCell } from '@salesforce/design-system-react';
import moment from 'moment';
import UpdateContact from '../../../../components/Modal/updateContact';
import ActionRemove from '../../../../components/Modal/actionRemove';
import { ACTION_TYPES } from '../../../../constants/types';
import { toast, Toaster } from 'react-hot-toast';
import { getCurrentUser } from '../../../../services/auth';
import WalletAddressEllipsis from '../../../../components/shared/WalletAddressEllipsis';
import AccountSvg from '../../../../assets/accounts';

const CustomNameCell = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell>
      <span className="slds-m-right_xx-small">
        <AccountSvg color={item.color || '#CCCCCC'} />
      </span>
      {children}
    </DataTableCell>
  );
};
CustomNameCell.displayName = DataTableCell.displayName;

const CustomDataTableAddress = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={children} {...props}>
      <a target="_blank" href={`http://debank.com/profile/${item.address}`}>
        <WalletAddressEllipsis text={children} maxTextCount={20} />
      </a>
    </DataTableCell>
  );
};
CustomDataTableAddress.displayName = DataTableCell.displayName;

const CustomDataTableDate = ({ children, ...props }) => {
  return <DataTableCell>{moment(children).format('MM/D/yyyy HH:mm:ss')}</DataTableCell>;
};
CustomDataTableDate.displayName = DataTableCell.displayName;

const CustomActionTableDate = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={'Actions'} {...props}>
      <Button
        className='action-pause-resume-button'
        key="Edit"
        label="Edit"
        responsive
        title="Edit contact"
        onClick={() =>
          props.setUpdateModal({
            open: true,
            contact: item,
          })
        }
        disabled={props.isSnapshot}
      />
      <Button
      className='action-delete-button'
        key="Remove"
        label="Remove"
        responsive
        variant="text-destructive"
        title="Remove contact"
        onClick={() =>
          props.setActionRemoveModal({
            open: true,
            type: ACTION_TYPES.REMOVE,
            name: item.name,
            id: item._id,
            title: 'Contact',
          })
        }
        disabled={props.isSnapshot}
      />
    </DataTableCell>
  );
};
CustomActionTableDate.displayName = DataTableCell.displayName;

const PermissionsTable = ({ isSnapshot }) => {
  const dispatch = useDispatch();
  const { contacts, loading, pagination, success, message } = useSelector(contactSelector);
  const [sortColumn, setSortColumn] = useState('opportunityName');
  const [sortColumnDirection, setSortColumnDirection] = useState({
    opportunityName: 'asc',
  });
  const [items, setItems] = useState([]);
  const [paginated, setPaginated] = useState(0);
  // const [page, setPage] = useState(0);
  const user = getCurrentUser();
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [loadingRemove, setLoadingRemove] = useState(false);
  const [updateContactModal, setUpdateContactModal] = useState({
    open: false
  });
  const [actionRemoveModal, setActionRemoveModal] = useState({
    open: false,
    type: '',
  });

  useEffect(() => {
    (user.role !== 'super_admin') && dispatch(getContacts());
  }, [dispatch]);


  useEffect(() => {
    setHasMore(true);
    if (contacts.length > 0) {
      setItems(contacts);
    } else {
      setItems([]);
    }
  }, [contacts, dispatch]);

  useEffect(() => {
    success &&
      showToast &&
      toast.success(message, {
        id: 'contact-delete-action',
      });
    setShowToast(false);
  }, [success]);

  const handleSort = (sortColumn, ...rest) => {
    const { property, sortDirection } = sortColumn;
    setSortColumn(property);
    setSortColumnDirection(sortDirection);
    setItems([]);
    setHasMore(true);
    setIsPageLoading(true);
    setPaginated(0);
    dispatch(getContacts({ sort_column: property, sort_order: sortDirection }));
  };

  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      if (pagination?.hasNextPage) {
        setIsPageLoading(true);
        setPaginated(paginated + 1);
        setTimeout(() => {
          if (pagination.pages <= 1 || paginated >= pagination.pages || !pagination.hasNextPage) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }

          if (pagination.currentPage >= pagination.pages) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }
          let url = new URLSearchParams(pagination.nextPageUrl);

          dispatch(
            getContacts({
              limit: url.get('limit'),
              offset: url.get('offset'),
              // q: searchText,
              sort_column: sortColumn,
              sort_order: sortColumnDirection,
            })
          );
          setIsPageLoading(false);
        }, 1000);
      } else {
        setIsPageLoading(false);
        setHasMore(false);
      }
    }
  };


  const removeContact = (id) => {
    // dispatch remove contact API
    if (id) {
      setLoadingRemove(true);
      dispatch(deleteContact(id)).then((res) => {
        if (res?.payload?.status === 200) {
          setShowToast(true);
          toast.success('Contact successfully removed', {
            id: 'contact-delete-action',
          });
          setActionRemoveModal({
            open: false,
            type: '',
          });
          dispatch(getContacts());
          setLoadingRemove(false);
        }
      })
    }
  }

  return (
    <div className="table-container">
      <Toaster position="top-right" reverseOrder={false} />
      <div style={{ height: `${contacts.length === 0 ? '100%' : 'calc(100vh - 255px)'}` }}>
        <DataTable
          assistiveText={{
            actionsHeader: 'actions',
            columnSort: 'sort this column',
            columnSortedAscending: 'asc',
            columnSortedDescending: 'desc',
            selectAllRows: 'Select all rows',
            selectRow: 'Select this row',
          }}
          fixedHeader
          fixedLayout
          items={items}
          id="DataTableExample-FixedHeaders"
          onSort={handleSort}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          noRowHover
        >
          <DataTableColumn sortable isDefaultSortDescending label="Name" property="name" width="10rem">
            <CustomNameCell />
          </DataTableColumn>
          <DataTableColumn
            sortable
            isDefaultSortDescending
            label="Wallet Type"
            property="wallet_type"
            width="8rem"
          />
          <DataTableColumn label="Chain" property="chain" width="6rem" />
          <DataTableColumn label="Accepted Assets" property="accepted_assets" width="8rem" />
          <DataTableColumn label="Category" property="contact_category" width="6rem" />
          <DataTableColumn label="Address" property="address" width="9rem">
            <CustomDataTableAddress />
          </DataTableColumn>
          <DataTableColumn label="Date Added" property="createdAt" sortable isDefaultSortDescending width="8rem">
            <CustomDataTableDate />
          </DataTableColumn>
          {(user.account_type = 'organization' && user.role !== 'user') && (
            <DataTableColumn label="Action" width="12rem">
              <CustomActionTableDate
                setActionRemoveModal={setActionRemoveModal}
                setUpdateModal={setUpdateContactModal}
                isSnapshot={isSnapshot}
              />
            </DataTableColumn>
          )}
        </DataTable>
        {contacts.length === 0 && !hasMore && (
          <div className='nodatafound' style={{marginTop: '90px'}}>
            <div>
              <img
                src="/assets/empty-state/empty-folder.png"
                alt=""
                style={{ height: '150px', display: 'flex', margin: 'auto' }}
              />
              <p style={{ textAlign: 'center', fontWeight: '700', fontSize: '20px' }}>
                Data not available
              </p>
            </div>
          </div>
        )}
      </div>

      <UpdateContact
        modelData={updateContactModal}
        closeModal={setUpdateContactModal}
      />

      <ActionRemove
        modelData={actionRemoveModal}
        closeModal={setActionRemoveModal}
        removeData={removeContact}
        isLoading={loadingRemove}
      />
    </div>
  );
};

export default PermissionsTable;
